import { useCallback, useMemo, useState } from "react";
import { useI18n } from "@core/i18n/i18n";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { ExtendedPatientProgram } from "@domain/program/patient-program";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";

export enum ProgramSortKey {
	LastEdited = "LastEdited",
	ProgramName = "ProgramName",
	PatientName = "PatientName",
}

type SortOption = {
	label: string;
	key: ProgramSortKey;
};

type StrictSortOption<K extends ProgramSortKey> = {
	label: string;
	key: K;
};

type AllSortOptions = SortOption[];

export function useProgramSort(programs: ExtendedPatientProgram[]): {
	allSortOptions: AllSortOptions;
	setSortKey: (key: ProgramSortKey) => void;
	sortedPrograms: ExtendedPatientProgram[];
} {
	const { format } = useI18n();
	const collator = useMemo(() => new Intl.Collator(), []);
	const { isDesktop } = useDimensions();
	const [sortKey, setSortKey] = useState(ProgramSortKey.LastEdited);
	const { formatPatientName } = useFormatPatients();

	const compareByLastEdited = useCallback(
		(a: ExtendedPatientProgram, b: ExtendedPatientProgram) =>
			b.lastEditionMetadata.date.diff(a.lastEditionMetadata.date),
		[],
	);

	const compareByProgramName = useCallback(
		(a: ExtendedPatientProgram, b: ExtendedPatientProgram) => {
			const result = collator.compare(a.name, b.name);
			if (result !== 0) return result;
			return compareByLastEdited(a, b);
		},
		[collator, compareByLastEdited],
	);

	const compareByPatientName = useCallback(
		(a: ExtendedPatientProgram, b: ExtendedPatientProgram) => {
			const result = collator.compare(formatPatientName(a.patient), formatPatientName(b.patient));
			if (result !== 0) return result;
			return compareByLastEdited(a, b);
		},
		[collator, compareByLastEdited, formatPatientName],
	);

	const sortedPrograms = useMemo(() => {
		switch (sortKey) {
			case ProgramSortKey.LastEdited:
				return [...programs].sort((a, b) => compareByLastEdited(a, b));
			case ProgramSortKey.ProgramName:
				return [...programs].sort((a, b) => compareByProgramName(a, b));
			case ProgramSortKey.PatientName:
				return [...programs].sort((a, b) => compareByPatientName(a, b));
		}
	}, [sortKey, programs, compareByLastEdited, compareByProgramName, compareByPatientName]);

	const allSortOptions = useMemo(
		() =>
			isDesktop
				? Object.values({
						[ProgramSortKey.LastEdited]: {
							label: format("templateListing.programs.sort.lastEdited"),
							key: ProgramSortKey.LastEdited,
						},
						[ProgramSortKey.ProgramName]: {
							label: format("templateListing.programs.sort.programName"),
							key: ProgramSortKey.ProgramName,
						},
						[ProgramSortKey.PatientName]: {
							label: format("templateListing.programs.sort.patientName"),
							key: ProgramSortKey.PatientName,
						},
					} as { [K in ProgramSortKey]: StrictSortOption<K> })
				: [],
		[format, isDesktop],
	);

	return {
		allSortOptions: allSortOptions,
		setSortKey,
		sortedPrograms,
	};
}
